import {
  Campaign,
  CampaignBody,
  CampaignType,
  CategoryLinkType,
  CategoryTreeObject,
  CategoryType,
  LinkedCategory,
} from "@vviinn/user-managment-client-ts";
import {
  ResponseMessageData,
  responseNotification,
} from "../../../components/Notifications";

import { linkCategory, unLinkCategory } from "../categories";
import { addLinkedCategory, removeLinkedCategory } from "./campaigns-slice";
import { RequestHandlerParams } from "../index";
import { upsertCampaign } from "./upsert";
import { deleteCampaign } from "./delete";
import { CampaignRequest } from "./types";

type RequestInputData = { requestType: string; dataToUpdate: CampaignBody };

type CustomMessageType = {
  info?: string;
  resultMessages?: ResponseMessageData;
};

type NecessaryBodyData = Pick<
  CampaignBody,
  "id" | "type" | "preferredImage" | "autoSuggestCampaign"
>;

const actionDescriptions = {
  assignCategory: {
    info: `Linking category`,
    successMessage: "Category linked",
    errorMessage: "Could not link category",
  },
  unassignCategory: {
    info: "Unlinking category",
    successMessage: "Category unlinked",
    errorMessage: "Could not unlink category",
  },
};

//for foreseeable future if one of this was sent to backend - all need to be sent, otherwise they could be overwritten
const relatedCampaignFields: (keyof CampaignBody)[] = [
  "enabledFilterColumns",
  "numberOfDisplayedDynamicFilters",
  "filterSorting",
];

export const handleCampaign = (
  params: RequestHandlerParams,
  { companyId, siteId }: CampaignRequest,
) => {
  const { getApi, notify, dispatch } = params;
  const campaignData = (campaign: Campaign) => {
    const campaignType = campaign.type ?? "VPR";
    const necessaryRequestData = { companyId, siteId, campaignType };
    return { campaignType, necessaryRequestData };
  };

  return {
    async handleUpsertCampaignData(
      campaign: Campaign,
      requestData: RequestInputData,
      customMessages?: CustomMessageType,
    ) {
      const api = await getApi();
      const { campaignType, necessaryRequestData } = campaignData(campaign);
      let necessaryBodyData: NecessaryBodyData = {
        id: campaign.id,
        type: campaignType,
      };
      if (!necessaryRequestData) return false;
      const uncoveredRelatedFields = relatedCampaignFields.filter(
        (key) => !(requestData.dataToUpdate[key] !== undefined),
      );
      if (
        uncoveredRelatedFields.length > 0 &&
        uncoveredRelatedFields.length < relatedCampaignFields.length
      )
        requestData.dataToUpdate = {
          ...requestData.dataToUpdate,
          ...Object.fromEntries(
            uncoveredRelatedFields.map((field) => [
              field,
              (campaign as CampaignBody)[field],
            ]),
          ),
        };

      if (campaign.type === "TEXT_SEARCH") {
        const { preferredImage, autoSuggestCampaign } = campaign;
        necessaryBodyData = {
          ...necessaryBodyData,
          preferredImage,
          autoSuggestCampaign,
        };
      }
      notify("Info", customMessages?.info ?? "Updating campaign");

      const response = await dispatch(
        upsertCampaign({
          api,
          value: {
            ...necessaryRequestData,
            campaignBody: { ...necessaryBodyData, ...requestData.dataToUpdate },
          },
        }),
      );
      const requestResult = response.meta.requestStatus === "fulfilled";
      responseNotification(
        notify,
        requestResult,
        customMessages?.resultMessages ?? requestData.requestType,
      );
      if (response.payload) return response.payload as Campaign;
      return false;
    },

    async handleCategoryAction(
      campaign: Campaign,
      actionTitle: keyof typeof actionDescriptions,
      categoryType: CategoryType,
      treeNode: CategoryTreeObject,
      activeTab: CategoryLinkType,
    ) {
      const api = await getApi();
      const campaignId = campaign.id;
      const categoryCode = treeNode.categoryCode;
      const optionalCampaignType = campaign.type;

      const body = {
        companyId,
        siteId,
        campaignId,
        categoryCode,
        categoryLinkType: activeTab as CategoryLinkType,
        categoryType,
        optionalCampaignType,
      };
      const { info, successMessage, errorMessage } =
        actionDescriptions[actionTitle];

      notify("Info", info);

      const category: LinkedCategory = {
        code: treeNode.categoryCode,
        subcategoriesCount: treeNode.tree.length,
      };
      const changeLinkedCategoryBody = {
        campaignId,
        category,
        type: activeTab,
        categoryType,
      };
      const assignCategory = async () => {
        const response = await dispatch(linkCategory({ api, value: body }));
        const requestResult = response.meta.requestStatus === "fulfilled";
        if (requestResult) {
          dispatch(
            addLinkedCategory({
              ...changeLinkedCategoryBody,
            }),
          );
        }
        return requestResult;
      };
      const unassignCategory = async () => {
        const response = await dispatch(unLinkCategory({ api, value: body }));
        const requestResult = response.meta.requestStatus === "fulfilled";
        if (requestResult) {
          dispatch(
            removeLinkedCategory({
              ...changeLinkedCategoryBody,
            }),
          );
        }
        return requestResult;
      };

      const actionResult =
        actionTitle === "assignCategory"
          ? await assignCategory()
          : await unassignCategory();

      responseNotification(notify, actionResult, {
        successMessage,
        errorMessage,
      });
    },

    async handleCampaignDeletion(campaign: Campaign) {
      const { necessaryRequestData } = campaignData(campaign);
      if (!necessaryRequestData) return false;
      const api = await getApi();
      const response = await dispatch(
        deleteCampaign({
          api,
          value: {
            ...necessaryRequestData,
            campaignId: campaign.id,
          },
        }),
      );
      return response.meta.requestStatus === "fulfilled";
    },

    async createCampaign(
      campaignType: CampaignType,
      campaignBody: CampaignBody,
    ) {
      const body = {
        companyId,
        siteId: siteId,
        campaignType,
        campaignBody,
      };
      const api = await getApi();

      const response = await dispatch(upsertCampaign({ api, value: body }));
      return response.payload;
    },
  };
};
